import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { portfolioData } from '../data/portfolioData';
import Navigation from './Navigation';
import { buildFortyTwoExperiences, formatKoreanDate } from '../data/fortyTwoProjects';
import ExamAttemptChart from './ExamAttemptChart';
import { useColorTheme } from '../hooks/useColorTheme';

// 다운로드 창(PDF·Word 라이브러리·6개 언어 번역표)은 창을 열 때만 불러온다. 처음 여는 동안은 모달과 같은 어두운 배경에 빙글 도는 표시만 (CLAUDE.md Phase 18)
// 새 배포 뒤 예전 화면을 열어 둔 탭은 사라진 예전 묶음 이름을 요청해 불러오기가 실패한다 → 온라인이면 한 번만 새로고침해 새 파일을 받는다
// (sessionStorage 표식으로 무한 새로고침을 막고, 성공하면 지운다. 오프라인이거나 이미 새로고침했으면 오류를 던져 AppErrorBoundary 가 화면을 다시 그린다)
const DOWNLOAD_MODAL_RELOADED = 'webPortfolio.downloadModalReloaded';
const ResumeDownloadModal = lazy(() => import('./ResumeDownloadModal').then(
  (module) => {
    try { sessionStorage.removeItem(DOWNLOAD_MODAL_RELOADED); } catch { /* 저장소를 못 써도 동작에는 지장 없다 */ }
    return module;
  },
  (error) => {
    let alreadyReloaded = true;
    try { alreadyReloaded = sessionStorage.getItem(DOWNLOAD_MODAL_RELOADED) === '1'; } catch { /* 저장소를 못 쓰면 새로고침하지 않는다 */ }
    if (!alreadyReloaded && navigator.onLine) {
      try { sessionStorage.setItem(DOWNLOAD_MODAL_RELOADED, '1'); } catch { /* 읽기가 됐으면 쓰기도 된다 */ }
      window.location.reload();
      return new Promise(() => {}); // 새로고침되는 동안 빙글 도는 표시를 그대로 둔다
    }
    throw error;
  },
));
const DownloadModalLoading = () => (
  <div className="fixed inset-0 bg-black/60 z-[10000] flex items-center justify-center" role="status" aria-label="불러오는 중">
    <div className="w-10 h-10 rounded-full border-4 border-white/30 border-t-white animate-spin" />
  </div>
);

// 설명의 "@기관" 이 한 단어로 줄바꿈되게 @ 바로 뒤(공백이 아닌 글자 앞)에 단어 결합자 U+2060(보이지 않음)을 넣는다.
// break-keep 이어도 브라우저는 기호 @ 와 한글 사이를 줄바꿈 자리로 봐서 "커뮤니티 @ / 중소벤처기업부" 처럼 끊는다 (CLAUDE.md Phase 11)
const keepAtWord = (text) => (typeof text === 'string' ? text.replace(/@(?=\S)/g, '@\u2060') : text);

// 증명서 링크에 보일 파일명 = 경로의 마지막 부분. 길고 공백이 적어 좁은 화면에서 넘치지 않게 "_"·"-" 뒤에만 줄바꿈 지점(<wbr>)을 넣는다.
// 정규식 lookbehind 는 쓰지 않는다(iOS 16.4 미만 Safari 에서 번들 전체가 구문 오류로 멈춘다) (CLAUDE.md Phase 12)
const fileNameOf = (path) => path.split('/').pop();
const withFileNameBreaks = (name) => {
  const parts = name.match(/[^_-]*[_-]|[^_-]+$/g) || [name];
  return parts.flatMap((part, i) => (i < parts.length - 1 ? [part, <wbr key={i} />] : [part]));
};
// 첨부 종류 아이콘: 이미지면 🖼️, 그 밖의 문서는 📄 (CLAUDE.md Phase 13)
const fileIconOf = (path) => (/\.(jpe?g|png|gif|webp)$/i.test(path) ? '🖼️' : '📄');

function Resume() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  // 테마는 모든 페이지가 함께 쓰고 기억한다(페이지를 옮겨도 유지, CLAUDE.md Phase 22)
  const { colorTheme, systemDarkMode, currentTheme, toggleColorTheme } = useColorTheme();
  const [activeSection, setActiveSection] = useState('Resume');
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [highlightEdu, setHighlightEdu] = useState(false);
  const highlightEduRef = React.useRef(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  // Expose toggle function globally
  React.useEffect(() => {
    window.toggleEduHighlight = () => {
      const newValue = !highlightEduRef.current;
      highlightEduRef.current = newValue;
      setHighlightEdu(newValue);

      setToastMessage(newValue
        ? 'Edu에 해당하는 경험을 타임라인에서 하이라이트 하였습니다'
        : '하이라이트를 해제하였습니다'
      );
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
      }, 2000);

      setActiveSection('Experience');
      setTimeout(() => {
        document.getElementById('experience')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    };

    // Check if loaded with #edu hash
    if (window.location.hash === '#edu') {
      highlightEduRef.current = true;
      setHighlightEdu(true);
      setToastMessage('Edu에 해당하는 경험을 타임라인에서 하이라이트 하였습니다');
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
      }, 2000);
      setActiveSection('Experience');
      setTimeout(() => {
        document.getElementById('experience')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }

    return () => {
      delete window.toggleEduHighlight;
    };
  }, []);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 640);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Scroll spy to track active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;

      // If at the very top, always show "Resume"
      if (scrollPosition < 10) {
        setActiveSection('Resume');
        return;
      }

      // Get all section positions
      const whyMeEl = document.getElementById('why_me');
      const skillsEl = document.getElementById('skills');
      const experienceEl = document.getElementById('experience');

      const whyMeTop = whyMeEl ? whyMeEl.offsetTop : Infinity;
      const skillsTop = skillsEl ? skillsEl.offsetTop : Infinity;
      const experienceTop = experienceEl ? experienceEl.offsetTop : Infinity;

      const offset = 150; // Header offset + buffer

      // Determine which section we're in
      if (scrollPosition >= experienceTop - offset) {
        setActiveSection('Experience');
      } else if (scrollPosition >= skillsTop - offset) {
        setActiveSection('Skills');
      } else if (scrollPosition >= whyMeTop - offset) {
        setActiveSection('Why Me?');
      } else {
        setActiveSection('Resume');
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Check hash on mount and scroll to section if needed
    const hash = window.location.hash;
    if (hash === '#why_me') {
      setActiveSection('Why Me?');
      setTimeout(() => {
        document.getElementById('why_me')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else if (hash === '#skills') {
      setActiveSection('Skills');
      setTimeout(() => {
        document.getElementById('skills')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else if (hash === '#experience') {
      setActiveSection('Experience');
      setTimeout(() => {
        document.getElementById('experience')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      setActiveSection('Resume');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Hash 변경 감지 - 다른 해시로 이동 시 하이라이트 해제 및 스크롤
  useEffect(() => {
    const hash = location.hash;

    // 하이라이트 해제 로직
    if (hash !== '#edu' && hash !== '') {
      highlightEduRef.current = false;
      setHighlightEdu(false);
    }

    // 해시에 따라 스크롤
    if (hash === '#why_me') {
      setActiveSection('Why Me?');
      setTimeout(() => {
        document.getElementById('why_me')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else if (hash === '#skills') {
      setActiveSection('Skills');
      setTimeout(() => {
        document.getElementById('skills')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else if (hash === '#experience') {
      setActiveSection('Experience');
      setTimeout(() => {
        document.getElementById('experience')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location]);

  // 다른 페이지(홈·Contacts)의 Resume → Download 로 왔으면 다운로드 창을 연다. 그 표시는 바로 지워, 새로고침·뒤로/앞으로 가기로 이 기록에 돌아와도 창이 다시 열리지 않게 (CLAUDE.md Phase 24)
  useEffect(() => {
    if (location.state?.openDownloadModal) {
      setShowDownloadModal(true);
      navigate(`${location.pathname}${location.search}${location.hash}`, { replace: true, state: null });
    }
  }, [location, navigate]);

  // Why Me 데이터
  const myWeaknesses = [
    { id: 1, text: '학업이 남들에 비해 늦은 상태' },
    { id: 2, text: '준비할 수 없는 과도한 압박에 취약' },
    { id: 3, text: '그냥 넘어갈 수 있는 것도 너무 꼼꼼함' }
  ];

  const competitorWeaknesses = [
    { id: 1, text: '엔지니어로서는 물경력' },
    { id: 2, text: '압박에도 견디고 성과를 내나 임시방편일 뿐' },
    { id: 3, text: '데드라인에 맞춘 결과물이더라도 숨은 이슈들' }
  ];

  const myStrengths = [
    { id: 1, text: '원가족을 포함한 신경 쓸 가족이 없어서 더 자유로운 활동 가능 (예: 추석연휴에도 근무 가능)' },
    { id: 2, text: '근본 있는 SW엔지니어로서의 교육 (예: POCU아카데미 학기제 수강 중, 42경산 라피신)' },
    { id: 3, text: '남들의 학업기간동안 저는 야생의 사회를 겪은 지라 법률문제를 포함한 각종 이슈에 대비 및 대응 가능' },
    { id: 4, text: '준 원어민 수준의 능통한 영어' },
    { id: 5, text: '아직 경력없는 학생 신분인지라 최저 임금부터 시작' }
  ];

  const competitorStrengths = [
    { id: 1, text: '어느정도 인정할만한 학력 또는 경력이 존재함' },
    { id: 2, text: '계약시 참고할만한 과거 체결된 임금 데이터가 있음' },
    { id: 3, text: '인정할만한 포트폴리오가 존재함' }
  ];

  // Experience 데이터 (기존 카드)
  // id: 다운로드 이력서(ResumeDownloadModal)의 번역표가 카드를 찾는 열쇠 — 바꾸면 resumeDownloadTranslations.js 도 같이 (CLAUDE.md Phase 17)
  const baseExperiences = [
    {
      id: 'modoo_startup',
      period: '2026년 9월 15일',
      duration: '', // 증명서에 결과 통보일이 없어 기간 길이를 비운다(빈 값이면 괄호 줄을 그리지 않는다)
      title: '모두의창업 2차 예비당선인 선발된 후 피드백과 함께 탈락',
      files: ['/certificates/도전경력증명서-모두의창업2차2026_이지연.pdf', '/certificates/모두의창업2차-아이디어_심사_의견.jpeg'], // 설명 아래 파일명 링크로 내려받는다(증명서, 아이디어 심사 의견 캡처)
      description: '아이디어: 내 프로젝트·습관의 성장을 나무의 성장에 빗대어 시각화하는 계획 앱이자, 그 성장을 자랑할 수 있는 온라인 커뮤니티 @중소벤처기업부 모두의 창업 프로젝트',
      link: 'https://www.modoo.or.kr/member/%EC%9D%B4%EC%98%A4%EB%9A%9D',
      linkLabel: '모두의창업 웹사이트의 내 프로필', // 주소 대신 보여 줄 글자
      review: {
        heading: '아이디어 심사 의견',
        source: '주식회사 영남대학교기술지주 최종 심사 의견',
        // 첨부한 캡처(모두의창업2차-아이디어_심사_의견.jpeg)의 세 항목을 번역되도록 원문 그대로 옮긴다 (CLAUDE.md Phase 14)
        items: [
          '학력이나 외적 성과가 나타난 사람 중심의 기존 플랫폼 한계를 넘어, 만달아트 기반의 일상, 습관 성장을 나무와 숲이라는 그래픽으로 시각화하여 커리어나 소통으로 연결하려는 차별성이 돋보이는 아이디어로 생각됨.',
          '기업/헤드헌터 대상 검색 수수료, 광고 등 다각화된 수익화 방향 구상도 좋으나 실제 구직 시장에서 실용성과 사업성에 대해서는 회의적임. 먼저 기업 및 헤드헌터는 검증된 실제 성과와 이력을 바탕으로 채용을 진행하므로 개인의 주관적인 습관이나 기록을 보고 스카우팅 제안을 하거나 비용을 지불할 가능성은 이윤을 추구하는 기업 입장에서 낮으며 헤드헌터 입장에서도 낮다고 보임.',
          '사회적 측면에서 소외계층에 대해 기회제공 측면에서 필요하다고는 판단되며 헤드헌터가 신뢰할 수 있는 데이터 검증 방식 및 타깃 유저 확보를 위한 비용확보를 위해 사회적기업가 육성사업 등 연계를 추천함.'
        ]
      },
      skills: ['#창업', '#사업기획', '#문제해결'],
      color: 'green',
      isEducation: false
    },
    {
      id: 'cadet',
      period: '2025년 12월 15일 ~ 현재',
      duration: '진행 중',
      title: '42경산 3기C 본과정 Cadet',
      files: ['/certificates/42경산 본과정 재학증명서 및 성적표_이지연.pdf'],
      description: 'Common Core Curriculum 수학 중 @42경산',
      descriptionLink: 'https://42gyeongsan.kr/ko/page.do',
      skills: ['#C언어', '#알고리즘', '#유닉스', '#시스템프로그래밍', '#자기주도학습', '#문제해결'],
      color: 'cyan',
      isEducation: true,
      fortyTwoCadet: true
    },
    {
      id: 'pdf_sorter',
      period: '2025년 12월 3일 ~ 4일',
      duration: '약 2일',
      title: 'PDFsorterByExcel',
      description: '엑셀 데이터 기반 PDF 자동 분류 도구. Antigravity와 Claude Code(Sonnet 4.5)로 개발. 바이브코더들과 동일 과제를 각자 구현 후 체험을 공유하는 프로젝트. 실제 회사의 단순 반복 업무 자동화에 활용.',
      skills: ['#Python', '#PDF처리', '#업무자동화', '#OCR', '#문제해결', '#자기주도학습', '#Antigravity', '#ClaudeCode'],
      color: 'purple',
      isEducation: false,
      link: 'https://github.com/SnowyCommander/PDFsorterByExcel'
    },
    {
      id: 'la_piscine',
      period: '2025년 10월 20일 ~ 11월 14일',
      duration: '약 4주',
      title: '라피신(예비과정) 수료',
      files: ['/certificates/42경산 라피신 교육참여확인서_이지연.pdf'],
      description: '@42경산',
      descriptionLink: 'https://42gyeongsan.kr/ko/page.do',
      skills: ['#C언어', '#프로그래밍', '#협업', '#자기주도학습', '#문제해결'],
      color: 'cyan',
      isEducation: true
    },
    {
      id: 'unheard_sounds',
      period: '2025년 10월 10일 ~ 12일',
      duration: '약 3일',
      title: '안들리는 연음 수집기',
      description: '유튜브를 통해 외국어를 학습하는 사람들을 위한 안들리는 연음 수집기',
      skills: ['#리액트', '#클로드 소넷4.5', '#버셀', '#자바스크립트', '#문제해결', '#자기주도학습'],
      color: 'blue',
      isEducation: false,
      link: 'https://unheard-sounds-collector.vercel.app/'
    },
    {
      id: 'comp1100',
      period: '2025년 10월 1일 ~ 현재',
      duration: '진행 중',
      title: 'COMP1100 공학용 수학 강좌만',
      description: '@POCU Teachable',
      skills: ['#수학', '#공학수학', '#자기주도학습', '#문제해결'],
      color: 'indigo',
      isEducation: true
    },
    {
      id: 'nested_garden',
      period: '2025년 10월 1일 ~ 현재',
      duration: '진행 중',
      title: '품은정원 NestedGarden',
      description: '시어핀스키 카펫 플래너로 보이지 않는 성장을 시각화하고, 커뮤니티를 형성하며, 헤드헌터에게 정보를 노출시켜 채용 기회를 만드는 웹앱',
      skills: ['#리액트', '#클로드 소넷4.5', '#클라우드플레어', '#자바스크립트', '#커뮤니티', '#문제해결', '#자기주도학습'],
      color: 'green',
      isEducation: false,
      link: 'https://nestedgarden.com'
    },
    {
      id: 'web_portfolio',
      period: '2025년 8월 ~ 10월 2일',
      duration: '약 2개월',
      title: 'webPortfolio 제작',
      description: 'emergent.sh와 클로드 소넷4.5를 활용하여 개인 포트폴리오 웹사이트 제작',
      skills: ['#리액트', '#클로드 소넷4.5', '#emergent.sh', '#자바스크립트', '#테일윈드CSS', '#깃', '#HTML5', '#CSS3', '#문제해결', '#자기주도학습'],
      color: 'blue',
      isEducation: false
    },
    {
      id: 'comp1500',
      period: '2025년 5월 ~ 8월',
      duration: '약 4개월',
      title: 'COMP1500 C# 입문 학기제 수료',
      description: '@POCU Academy',
      skills: ['#C#', '#프로그래밍', '#자기주도학습', '#문제해결'],
      color: 'indigo',
      isEducation: true
    },
    {
      id: 'officetel',
      period: '2022년 5월 6일 ~ 현재',
      duration: '진행 중',
      title: '오피스텔 임대 사업', // jy 가 제목·기간만 줬다 → 설명·Skills·링크 없음 (CLAUDE.md Phase 15)
      color: 'green',
      isEducation: false
    },
    {
      id: 'unica',
      period: '2017년 1월 ~ 5월',
      duration: '약 5개월',
      title: 'UNICA 영화제 출품작 영어자막 검수',
      description: '<산상수훈>의 영어자막 검수 작업 @그란 @영화로 세상을 아름답게 @UNICA',
      skills: ['#영어', '#번역', '#세심함', '#의사소통'],
      color: 'cyan',
      isEducation: false
    },
    {
      id: 'multifamily_housing',
      period: '2015년 1월 ~ 2026년 7월 7일',
      duration: '약 11년 6개월', // 2015-01-01 ~ 2026-07-07 (다른 카드처럼 시작 달의 1일부터 센다)
      title: '다가구 주택 신축 가족 사업 계획 및 관련 소송들',
      description: '관련 활동들',
      descriptionItems: ['시장 조사', '사업 공부', '건축 자재별 건축물 특징 및 비용 공부', '예산 계산', '각종 소송'], // 설명 아래 글머리표 목록 (jy 문장 그대로. "자재별" 은 jy 지시로 표준 띄어쓰기 — CLAUDE.md Phase 16)
      skills: ['#프로젝트관리', '#법률지식', '#법률행위', '#문제해결', '#의사소통', '#사업기획'],
      color: 'green',
      isEducation: false
    }
  ];

  // 42 Common Core 과제 카드 (scripts/fetch42Projects.mjs 가 만든 데이터). isEducation: true 라 Edu 하이라이트가 그대로 적용된다.
  // fortyTwoCadet 카드(42경산 본과정 Cadet) 바로 뒤에 시작일 내림차순으로 끼운다. 최상위 카드는 시작일 내림차순이라 Cadet 보다 최근 카드가 앞에 올 수 있어 위치를 찾아서 끼운다 (CLAUDE.md Phase 11)
  const cadetIndex = baseExperiences.findIndex((exp) => exp.fortyTwoCadet);
  const experiences = [
    ...baseExperiences.slice(0, cadetIndex + 1),
    ...buildFortyTwoExperiences(),
    ...baseExperiences.slice(cadetIndex + 1)
  ];

  const getColorClasses = (color, theme) => {
    const colors = {
      blue: {
        light: 'bg-blue-100 border-blue-300',
        dark: 'bg-blue-900/30 border-blue-700',
        dot: 'bg-blue-500'
      },
      indigo: {
        light: 'bg-indigo-100 border-indigo-300',
        dark: 'bg-indigo-900/30 border-indigo-700',
        dot: 'bg-indigo-500'
      },
      cyan: {
        light: 'bg-cyan-100 border-cyan-300',
        dark: 'bg-cyan-900/30 border-cyan-700',
        dot: 'bg-cyan-500'
      },
      purple: {
        light: 'bg-purple-100 border-purple-300',
        dark: 'bg-purple-900/30 border-purple-700',
        dot: 'bg-purple-500'
      },
      green: {
        light: 'bg-green-100 border-green-300',
        dark: 'bg-green-900/30 border-green-700',
        dot: 'bg-green-500'
      }
    };

    return colors[color][theme === 'dark' ? 'dark' : 'light'];
  };

  const getDotColor = (color) => {
    const colors = {
      blue: 'bg-blue-500',
      indigo: 'bg-indigo-500',
      cyan: 'bg-cyan-500',
      purple: 'bg-purple-500',
      green: 'bg-green-500'
    };
    return colors[color];
  };

  // Count skills from all experiences
  const countSkills = () => {
    const skillCounts = {};
    experiences.forEach(exp => {
      if (exp.skills) {
        exp.skills.forEach(skill => {
          const cleanSkill = skill.replace('#', '');
          skillCounts[cleanSkill] = (skillCounts[cleanSkill] || 0) + 1;
        });
      }
    });
    // Sort by count descending, then alphabetically
    return Object.entries(skillCounts)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([skill, count]) => ({ skill, count }));
  };

  const skillsList = countSkills();

  // Handle skill click
  const handleSkillClick = (skill) => {
    let newSelectedSkills;
    let message;

    if (selectedSkills.includes(skill)) {
      // Deselect skill
      newSelectedSkills = selectedSkills.filter(s => s !== skill);
      message = `#${skill} 선택 해제됨`;
    } else {
      // Select skill
      newSelectedSkills = [...selectedSkills, skill];
      message = `#${skill} 하이라이트됨`;
    }

    setSelectedSkills(newSelectedSkills);
    setToastMessage(message);
    setShowToast(true);

    // Auto-dismiss toast after 2 seconds
    setTimeout(() => {
      setShowToast(false);
    }, 2000);
  };

  return (
    <>
      <Navigation
        navigation={portfolioData.navigation}
        isMobile={isMobile}
        personal={portfolioData.personal}
        colorTheme={colorTheme}
        currentTheme={currentTheme}
        systemDarkMode={systemDarkMode}
        toggleColorTheme={toggleColorTheme}
        onDownloadClick={() => setShowDownloadModal(true)}
      />

      {showDownloadModal && (
        <Suspense fallback={<DownloadModalLoading />}>
          <ResumeDownloadModal
            isOpen={showDownloadModal}
            onClose={() => setShowDownloadModal(false)}
            currentTheme={currentTheme}
            experiences={experiences}
            whyMe={{ myWeaknesses, competitorWeaknesses, myStrengths, competitorStrengths }}
          />
        </Suspense>
      )}

      {/* 데스크탑: 오른쪽 고정 사이드바 폭(--desktop-nav-width)만큼 본문을 비워 겹치지 않게 한다 (Phase 7) */}
      <section
        id="resume"
        className={`min-h-screen px-4 ${isMobile ? 'pb-20' : ''} ${
          currentTheme === 'dark'
            ? 'bg-gray-900 text-white'
            : 'bg-orange-50 text-gray-900'
        }`}
        style={isMobile ? undefined : { paddingRight: 'calc(1rem + var(--desktop-nav-width))' }}
      >
        {/* 타이틀 띠 - 데스크탑에서는 사이드바를 뺀 본문 폭 기준으로 중앙.
            화면 맨 위 48px 를 페이지 배경색으로 덮어, 스크롤되는 섹션 소제목·본문이 제목 밑으로 비쳐 두 겹으로 보이지 않게 한다(높이 = 섹션 scroll-mt-12).
            z-30: 본문(타임라인 z-10·20) 위, 메뉴 backdrop(z-40·60·95) 아래라 메뉴를 열면 함께 어두워진다 (CLAUDE.md Phase 14) */}
        <div
          className={`fixed top-0 left-0 right-0 z-30 h-12 flex items-center justify-center border-b ${
            currentTheme === 'dark' ? 'bg-gray-900 border-gray-800' : 'bg-orange-50 border-orange-100'
          }`}
          style={isMobile ? undefined : { right: 'var(--desktop-nav-width)' }}
        >
          <h2 className="text-xl font-bold">{activeSection}</h2>
        </div>

        {/* Toast Notification - 데스크탑에서는 본문 축에 맞춰 중앙.
            animate-fade-in-out 는 keyframe 의 transform 이 바깥 div 의 -translate-x-1/2 를 덮어쓰므로 안쪽 상자에 건다 */}
        {showToast && toastMessage && (
          <div
            className="fixed top-20 left-1/2 transform -translate-x-1/2 z-[9999] max-w-[90vw]"
            style={isMobile ? undefined : { left: 'calc(50% - var(--desktop-nav-width) / 2)' }}
          >
            <div className={`animate-fade-in-out px-6 py-3 rounded-lg shadow-lg ${
              currentTheme === 'dark'
                ? 'bg-gray-800 text-white border border-gray-700'
                : 'bg-white text-gray-900 border border-gray-300'
            }`}>
              <p className="text-sm font-medium break-keep">
                {toastMessage}
              </p>
            </div>
          </div>
        )}

        <div className="container mx-auto max-w-4xl space-y-12 pt-16">

          {/* 세 섹션의 scroll-mt-12(48px): 메뉴로 이동(scrollIntoView)하면 섹션 소제목이 화면 위 고정 제목 아래에 온다. 48px 이면 Why Me? 로 이동해도 scrollY 16px 이라 스크롤 스파이가 "Why Me?" 를 유지한다 (CLAUDE.md Phase 11) */}
          {/* Why Me 섹션 */}
          <div id="why_me" className="space-y-6 scroll-mt-12">
            <h3 className="text-xl font-bold text-center">Why Me?</h3>

            {/* 단점 그룹 */}
            <div className="space-y-0">
              {/* 저의 단점 섹션 */}
              <div className="p-6 rounded-t-lg bg-stone-400 space-y-6">
                <p className="text-left text-lg">저의 단점은...</p>
                <div className="space-y-4">
                  {myWeaknesses.map((item) => (
                    <p key={item.id} className="flex items-start">
                      <span className="mr-2 flex-shrink-0">○</span>
                      <span className="break-keep">{item.text}</span>
                    </p>
                  ))}
                </div>
              </div>

              {/* 그에 비해 경쟁자들의 단점 */}
              <div className="p-6 rounded-b-lg bg-gray-500/30 space-y-6">
                <p className="text-right text-lg">
                  그에 비해<br />
                  경쟁자들의<br />
                  단점은 대개...
                </p>
                <div className="space-y-4">
                  {competitorWeaknesses.map((item) => (
                    <p key={item.id} className="flex items-start text-left">
                      <span className="mr-2 flex-shrink-0">○</span>
                      <span className="break-keep">{item.text}</span>
                    </p>
                  ))}
                </div>
              </div>
            </div>

            {/* 장점 그룹 */}
            <div className="space-y-0">
              {/* 저의 장점 섹션 */}
              <div className="p-6 rounded-t-lg bg-amber-300 space-y-6">
                <p className="text-left text-lg text-black">저의 장점은...</p>
                <div className="space-y-4">
                  {myStrengths.map((item) => (
                    <p key={item.id} className="flex items-start text-black">
                      <span className="mr-2 flex-shrink-0">○</span>
                      <span className="break-keep">{item.text}</span>
                    </p>
                  ))}
                </div>
              </div>

              {/* 그에 비해 경쟁자들의 장점 */}
              <div className="p-6 rounded-b-lg bg-gray-300/60 space-y-6">
                <p className="text-right text-lg">
                  그에 비해<br />
                  경쟁자들의<br />
                  장점은 대개...
                </p>
                <div className="space-y-4">
                  {competitorStrengths.map((item) => (
                    <p key={item.id} className="flex items-start text-left">
                      <span className="mr-2 flex-shrink-0">○</span>
                      <span className="break-keep">{item.text}</span>
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Skills 섹션 */}
          <div id="skills" className="space-y-6 scroll-mt-12">
            <h3 className="text-xl font-bold text-center">Skills</h3>

            <div className={`p-6 rounded-lg ${
              currentTheme === 'dark'
                ? 'bg-gray-800/50'
                : 'bg-white/50'
            }`}>
              {/* 안내 문구 */}
              <p className={`text-sm text-center mb-4 ${
                currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                버튼을 누르시면 해당 스킬이 하단의 Experience timeline에서 하이라이트 되어 적용된 프로젝트들을 더욱 쉽게 발견하실 수 있게 됩니다.
              </p>

              <div className="flex flex-wrap gap-3 justify-center">
                {skillsList.map(({ skill, count }) => (
                  <button
                    key={skill}
                    onClick={() => handleSkillClick(skill)}
                    className={`px-4 py-2 rounded-lg border-2 transition-all cursor-pointer hover:scale-105 ${
                      selectedSkills.includes(skill)
                        ? 'bg-yellow-400 border-yellow-500 text-gray-900'
                        : currentTheme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
                        : 'bg-gray-100 border-gray-300 text-gray-800 hover:bg-gray-200'
                    }`}
                  >
                    <span className="font-semibold">#{skill}</span>
                    <span className={`ml-2 text-sm ${
                      selectedSkills.includes(skill)
                        ? 'text-gray-700'
                        : currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      ×{count}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Experience 섹션 */}
          <div id="experience" className="space-y-6 scroll-mt-12">
            <h3 className="text-xl font-bold text-center">Experience</h3>

            {/* Timeline */}
            <div className="relative">
              {/* Timeline line */}
              <div className={`absolute left-4 sm:left-1/4 top-0 bottom-0 w-0.5 z-10 ${
                currentTheme === 'dark' ? 'bg-gray-700' : 'bg-gray-300'
              }`}></div>

              {/* Experience items */}
              <div className="space-y-8">
                {experiences.map((exp, idx) => (
                  <div key={idx} className={`relative flex flex-col sm:flex-row items-start p-4 rounded-lg transition-all duration-300 ${
                    highlightEdu && exp.isEducation
                      ? (currentTheme === 'dark' ? 'bg-yellow-400/15 ring-2 ring-yellow-400/60' : 'bg-yellow-100')
                      : ''
                  }`}>
                    {/* Left side - Period info and Skills */}
                    <div className="w-full sm:w-1/4 pl-6 sm:pl-0 sm:pr-8 text-left sm:text-right">
                      <p className={`text-sm font-semibold break-keep ${
                        currentTheme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        {exp.period}
                      </p>
                      {exp.duration ? (
                        <p className={`text-xs mt-1 break-keep ${
                          currentTheme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                        }`}>
                          ({exp.duration})
                        </p>
                      ) : null}

                      {/* Skills */}
                      {exp.skills && exp.skills.length > 0 && (
                        <div className="mt-2 sm:mt-4">
                          <p className={`text-xs font-semibold mb-2 ${
                            currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            Skills
                          </p>
                          <div className="flex flex-wrap gap-1 justify-start sm:justify-end">
                            {exp.skills.map((skill, idx) => {
                              const cleanSkill = skill.replace('#', '');
                              const isHighlighted = selectedSkills.includes(cleanSkill);
                              return (
                                <span
                                  key={idx}
                                  className={`text-[10px] px-1.5 py-0.5 rounded transition-all ${
                                    isHighlighted
                                      ? 'bg-yellow-400 text-gray-900 font-bold'
                                      : currentTheme === 'dark'
                                      ? 'bg-gray-700 text-gray-300'
                                      : 'bg-gray-200 text-gray-700'
                                  }`}
                                >
                                  {skill}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Dot */}
                    <div className={`absolute left-4 sm:left-1/4 -ml-2.5 w-5 h-5 rounded-full z-20 ${getDotColor(exp.color)} border-4 ${
                      currentTheme === 'dark' ? 'border-gray-900' : 'border-orange-50'
                    }`}></div>

                    {/* Right side - Content */}
                    <div className="w-full sm:w-3/4 pl-6 sm:pl-8 mt-3 sm:mt-0">
                      <div className={`p-6 rounded-lg border-2 ${getColorClasses(exp.color, currentTheme)}`}>
                        {exp.fortyTwoSlug && (
                          <span className={`inline-block mb-2 px-2 py-0.5 rounded text-xs font-semibold break-keep ${
                            currentTheme === 'dark' ? 'bg-cyan-800 text-cyan-100' : 'bg-cyan-200 text-cyan-900'
                          }`}>
                            42 Common Core{exp.milestone != null ? ` · Milestone #${exp.milestone}` : ''}{exp.isExam ? ' · 시험' : ''}
                          </span>
                        )}
                        {/* 제목만 있는 카드(설명·첨부·링크 없음)는 last:mb-0 으로 상자 위아래 여백을 같게 한다 (CLAUDE.md Phase 15) */}
                        <h3 className="text-xl font-bold mb-3 last:mb-0 break-keep">
                          {exp.title}
                        </h3>
                        {exp.description && (
                          <p className="break-keep">
                            {exp.descriptionLink ? (
                              <a
                                href={exp.descriptionLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`underline hover:no-underline cursor-pointer ${
                                  currentTheme === 'dark' ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
                                }`}
                              >
                                {keepAtWord(exp.description)}
                              </a>
                            ) : (
                              keepAtWord(exp.description)
                            )}
                          </p>
                        )}
                        {exp.descriptionItems?.length > 0 && (
                          // 설명 아래 글머리표 목록(예: 다가구 카드의 "관련 활동들"). 심사 의견 목록과 같은 모양, 글자 크기는 설명과 같게 (CLAUDE.md Phase 15)
                          <ul className="mt-2 list-disc pl-5 space-y-1 break-keep">
                            {exp.descriptionItems.map((item, dIdx) => (
                              <li key={dIdx}>{keepAtWord(item)}</li>
                            ))}
                          </ul>
                        )}
                        {exp.files?.length > 0 && (
                          // 첨부 파일: 무엇을 내려받는지 보이게 종류 아이콘(🖼️ 이미지 / 📄 문서)과 파일명을 적고, 누르면 그 이름으로 내려받는다(같은 출처 파일만 가능, CLAUDE.md Phase 12·13)
                          <div className="mt-3 space-y-1">
                            {exp.files.map((file) => (
                              <p key={file} className="text-sm break-keep">
                                <a
                                  href={file}
                                  download={fileNameOf(file)}
                                  className={`underline hover:no-underline cursor-pointer ${
                                    currentTheme === 'dark' ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
                                  }`}
                                >
                                  {fileIconOf(file)} {withFileNameBreaks(fileNameOf(file))}
                                </a>
                              </p>
                            ))}
                          </div>
                        )}
                        {exp.link && (
                          // linkLabel 이 있으면 주소 대신 그 글자를 보여 준다. 한글 글자는 단어 단위로 줄바꿈(break-keep), 주소만 있으면 아무 데서나 끊는다(break-all) (CLAUDE.md Phase 11)
                          <a
                            href={exp.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`inline-block mt-3 text-sm underline hover:no-underline cursor-pointer ${exp.linkLabel ? 'break-keep' : 'break-all'} ${
                              currentTheme === 'dark' ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
                            }`}
                          >
                            🔗 {exp.linkLabel || exp.link}
                          </a>
                        )}
                        {exp.review && (
                          // 심사 의견: 첨부 캡처의 글자는 번역이 안 되므로 같은 내용을 글로 보여 준다(동료평가 블록과 같은 모양, CLAUDE.md Phase 14)
                          <div className={`mt-4 pt-3 border-t ${
                            currentTheme === 'dark' ? 'border-green-800' : 'border-green-300'
                          }`}>
                            <p className="text-sm font-semibold break-keep">{exp.review.heading}</p>
                            <p className={`text-sm break-keep ${
                              currentTheme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              {exp.review.source}
                            </p>
                            <ul className="mt-2 list-disc pl-5 space-y-1 text-sm break-keep">
                              {exp.review.items.map((item, rIdx) => (
                                <li key={rIdx}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {exp.evaluation && (
                          <div className={`mt-4 pt-3 border-t ${
                            currentTheme === 'dark' ? 'border-cyan-800' : 'border-cyan-300'
                          }`}>
                            <p className="text-sm font-semibold break-keep">동료평가</p>
                            <p className={`text-sm break-keep ${
                              currentTheme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              종합 점수 {exp.evaluation.finalMark ?? '-'} / 100 · 평가 {exp.evaluation.count}회
                              {exp.evaluation.outstanding > 0 && ` · Outstanding ${exp.evaluation.outstanding}회`}
                            </p>
                            {exp.evaluation.summaries.length > 0 && (
                              <ul className="mt-2 list-disc pl-5 space-y-1 text-sm break-keep">
                                {exp.evaluation.summaries.map((summary, sIdx) => (
                                  <li key={sIdx}>{summary}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}
                        {exp.exam && (
                          <div className={`mt-4 pt-3 border-t ${
                            currentTheme === 'dark' ? 'border-cyan-800' : 'border-cyan-300'
                          }`}>
                            <p className="text-sm font-semibold break-keep">시험 결과</p>
                            <p className={`text-sm break-keep ${
                              currentTheme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              최종 점수 {exp.exam.finalMark ?? '-'} / 100
                              {exp.exam.attempts ? ` · ${exp.exam.attempts}번째 응시에서 통과` : ''}
                              {exp.exam.passedOn ? ` (${formatKoreanDate(exp.exam.passedOn)})` : ''}
                            </p>
                            <ExamAttemptChart history={exp.exam.history} currentTheme={currentTheme} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </section>
    </>
  );
}

export default Resume;
